import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { IconButton } from "@/design-system/IconButton/IconButton";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { eventTokensSelector } from "@/redux/loadedContent/loadedContentSlice";
import { getUserSelector } from "@/redux/user/userSlice";
import {
  type GalleryCardGrade,
  type GalleryGrade,
  type GalleryGradeName,
  type GalleryListSet,
  type GallerySetProgress,
  getGallerySolution,
} from "@repo/shared/api";
import {
  GALLERY_TOKEN_ID,
  type GalleryVerdict,
  compact,
  completedGradeToggle,
  describeGalleryCard,
  gradeHintKey,
  isSolving,
  solvedAge,
} from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import {
  Ban,
  Check,
  Clock,
  Star,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
} from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router";

import { GalleryBadge } from "./GalleryBadge";
import { GalleryFirstOwnerSheet } from "./GalleryFirstOwnerSheet";
import { GalleryStatTip, statBox, statLabel } from "./GalleryStatTip";

// EA's in-game grade colours: bronze, silver, gold, teal, light purple.
const GRADE_COLOR: Record<GalleryGradeName, string> = {
  D: "text-[#c98a5e]",
  C: "text-[#c4ccd4]",
  B: "text-[#d9c37f]",
  A: "text-[#4fd1c5]",
  S: "text-[#b794f4]",
};

// EA's grade badge without its shine: a dark diamond in a metal frame with an inner line
// around the letter. Reachable grades are lit (also those needing cards the market doesn't
// sell), the selected one is outlined, completed ones are grey with a tick. Unreachable ones are
// faded and only selectable with pickUnreachable. Without onSelect it is a plain span, for use inside another button.
export const GradeDiamond = ({
  grade,
  selected,
  reachable,
  completed,
  onSelect,
  pickUnreachable = false,
}: {
  grade: GalleryGradeName;
  selected: boolean;
  reachable: boolean;
  completed: boolean;
  onSelect?: () => void;
  pickUnreachable?: boolean;
}) => {
  const Comp = onSelect ? "button" : "span";
  const pickable = reachable || completed || pickUnreachable;
  return (
    <Comp
      type={onSelect ? "button" : undefined}
      onClick={onSelect}
      disabled={onSelect ? !pickable : undefined}
      aria-label={grade}
      className={cn(
        "relative inline-flex size-8 shrink-0 items-center justify-center font-bold leading-none",
        completed ? "text-neutral-600" : GRADE_COLOR[grade],
        onSelect && (pickable ? "cursor-pointer" : "cursor-not-allowed"),
        !reachable && !completed && "opacity-25",
      )}
    >
      {/* SVG, not HTML text: Chrome rounds an HTML baseline to whole pixels, which lifted
          the letter ~0.4px. The tick is Lucide's, recentred. */}
      <svg
        viewBox="0 0 32 32"
        aria-hidden
        className="absolute inset-0 size-full"
      >
        {selected && (
          <path
            d="M16 0.75 31.25 16 16 31.25 0.75 16Z"
            fill="none"
            strokeWidth={1.25}
            className="stroke-white"
          />
        )}
        {/* The frame; from B up also an inner line with a faint wash of the metal (D and C are just the frame, like EA's). */}
        <path
          d="M16 3 29 16 16 29 3 16Z"
          strokeWidth={1.75}
          stroke="currentColor"
          className="fill-neutral-900"
        />
        {grade !== "D" && grade !== "C" && (
          <path
            d="M16 6 26 16 16 26 6 16Z"
            fill="currentColor"
            fillOpacity={0.1}
            stroke="currentColor"
            strokeOpacity={0.55}
            strokeWidth={0.6}
          />
        )}
        {completed ? (
          <path
            transform="translate(2 2)"
            d="M17.97 11.27 12.51 16.73 10.03 14.25"
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-streamline-success"
          />
        ) : (
          <text
            x={16}
            y={16}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fill="currentColor"
          >
            {grade}
          </text>
        )}
      </svg>
    </Comp>
  );
};

/**
 * The tick in the card's corner. One click completes the selected grade (and
 * every grade below it). On a grade already completed it unmarks every grade.
 */
export const CompletedGradePicker = ({
  grades,
  completedIndex,
  selectedIndex,
  onChange,
}: {
  grades: GalleryGrade[];
  completedIndex: number;
  selectedIndex?: number;
  onChange: (grade: GalleryGradeName | null) => void;
}) => {
  const { t } = useTranslation("gallery");
  const { selected, completed, next } = completedGradeToggle(
    grades,
    completedIndex,
    selectedIndex,
  );
  return (
    <IconButton
      aria-label={
        selected
          ? t(completed ? "completion.unmark" : "completion.mark", {
              grade: selected.name,
            })
          : t("completion.title")
      }
      disabled={!selected}
      onClick={() => selected && onChange(next)}
      icon={
        <Check
          className={cn(
            "size-4",
            completed ? "text-streamline-success" : "text-foreground-muted",
          )}
        />
      }
    />
  );
};

export const GalleryTokenIcon = ({ className }: { className?: string }) => {
  const eventTokens = useSelector(eventTokensSelector);
  const token = eventTokens.find(
    (e) => String(e.eventTokenId) === GALLERY_TOKEN_ID,
  );
  if (!token) return null;
  return (
    <img
      src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/${token.iconKey}`}
      alt={token.displayName}
      className={className}
    />
  );
};

// The green "Nothing to buy" banner over Coins Needed and Tax Loss; flip to hide it.
const SHOW_NOTHING_TO_BUY_BANNER = true;

const VERDICT_ICONS = { good: ThumbsUp, fair: ThumbsUp, bad: ThumbsDown };
export const VERDICT_COLORS = {
  good: "text-streamline-success",
  fair: "text-amber-400",
  bad: "text-red-400",
};

/** The Verdict box's tooltip: one line per icon. */
export const VerdictInfo = () => {
  const { t } = useTranslation("gallery");
  const rows = [
    {
      Icon: Star,
      color: "text-s-tier-foreground",
      text: t("verdictInfo.free"),
    },
    {
      Icon: ThumbsUp,
      color: "text-streamline-success",
      text: t("verdictInfo.good"),
    },
    { Icon: ThumbsUp, color: "text-amber-400", text: t("verdictInfo.fair") },
    { Icon: ThumbsDown, color: "text-red-400", text: t("verdictInfo.bad") },
  ];
  return (
    <div className="flex flex-col gap-y-1.5">
      <p>{t("verdictInfo.title")}</p>
      {rows.map(({ Icon, color, text }) => (
        <p key={text} className="flex items-center gap-x-2">
          <Icon className={cn("size-4 shrink-0", color)} />
          <span>{text}</span>
        </p>
      ))}
    </div>
  );
};

/** The EasySBC Verdict as an icon, its word for screen readers; "–" when there is none. */
export const Verdict = ({
  verdict,
  nothingToBuy,
  className,
}: {
  verdict?: GalleryVerdict;
  /** Every player collected: a star in the meta ratings' S tier colour instead. */
  nothingToBuy: boolean;
  className?: string;
}) => {
  const { t } = useTranslation("gallery");
  if (nothingToBuy)
    return (
      <Star
        aria-label={t("verdict.free")}
        className={cn("size-5 shrink-0 text-s-tier-foreground", className)}
      />
    );
  if (verdict === undefined)
    return (
      <span className="text-sm font-semibold text-foreground-faint">–</span>
    );
  const Icon = VERDICT_ICONS[verdict];
  return (
    <Icon
      aria-label={t(`verdict.${verdict}`)}
      className={cn("size-5 shrink-0", VERDICT_COLORS[verdict], className)}
    />
  );
};

// Memoized with stable handlers, so the ~127-card grid skips unrelated page re-renders.
export const GallerySetCard = memo(function GallerySetCard({
  set,
  categorySlug,
  progress,
  cardGrades,
  sortPick,
  onCompletedGradeChange,
  onFirstOwnersChanged,
  clubImported,
}: {
  set: GalleryListSet;
  categorySlug: string;
  progress?: GallerySetProgress;
  /** The user's stored solution, else the market-only solve. Grades come D first. */
  cardGrades: GalleryCardGrade[];
  /** The grade the active sort scored best, selected until the user picks another. */
  sortPick?: number;
  onCompletedGradeChange: (
    setId: number,
    grade: GalleryGradeName | null,
  ) => void;
  onFirstOwnersChanged: () => void;
  clubImported: boolean;
}) {
  const { t } = useTranslation("gallery");
  const refreshing = isSolving(progress?.solveStatus);
  const refreshError =
    progress?.solveStatus === "failed" ? t("solution.error") : undefined;
  const { user } = useSelector(getUserSelector);
  const [pickedIndex, setPickedIndex] = useState<number>();
  // An unsolvable set (no grade above the completed one has a solution) is dimmed, without
  // stats or solution link, the next grade selected. Its grades and first owners stay usable.
  const {
    completedIndex,
    unsolvable,
    selectedIndex,
    selected,
    tokens: bestTokens,
    tokensGained,
    verdict,
    showCollected,
    nothingToBuy,
    dimmed: completedShown,
  } = describeGalleryCard({
    grades: set.grades,
    cardGrades,
    completedGrade: progress?.completedGrade,
    hasSolution: !!progress?.solution,
    pickedIndex,
    sortPick,
  });
  // A holo border that fades in and out, and a tick, once a refresh lands.
  const [justRefreshed, setJustRefreshed] = useState(false);
  const [firstOwnersOpen, setFirstOwnersOpen] = useState(false);
  // The shown grade's lineup, read when the First Owner sheet opens.
  const [solutionIds, setSolutionIds] = useState<number[]>();
  const solutionRequest = useRef(0);
  const wasRefreshing = useRef(refreshing);
  useEffect(() => {
    const finished = wasRefreshing.current && !refreshing && !refreshError;
    wasRefreshing.current = refreshing;
    if (!finished) return;
    setJustRefreshed(true);
    const timeout = window.setTimeout(() => setJustRefreshed(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [refreshing, refreshError]);
  // A completed grade is every card collected, whatever the solution says.
  const collected = completedShown
    ? set.requiredCards
    : (selected?.collectedCount ?? 0);
  const collectedOf =
    completedShown || !selected?.reachable ? set.requiredCards : selected.size;
  // No solution built for this user yet: the card shows the market solution.
  const marketOnly = !progress?.solution;
  const solved = progress?.solution
    ? solvedAge(progress.solution.computedAt)
    : undefined;

  return (
    <Card
      hoverable={false}
      className={cn(
        "relative flex h-full flex-col gap-y-4 p-4",
        // Off-screen cards skip layout and paint; the clip margin keeps the refresh sweep on the border.
        "[content-visibility:auto] [contain-intrinsic-height:auto_300px] [overflow-clip-margin:1px]",
      )}
    >
      {justRefreshed && <span className="holo-border-sweep" />}
      {/* A completed grade dims every part of the card but the grades, which stay pickable. */}
      <div
        className={cn(
          "flex items-start gap-x-3",
          completedShown && "opacity-50",
        )}
      >
        <GalleryBadge badge={set.badge} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold">{set.name}</h3>
          <p className="text-sm text-foreground-muted">
            {t("list.requiredCards", { count: set.requiredCards })}
          </p>
        </div>
        {!!user.uuid && (
          <CompletedGradePicker
            grades={set.grades}
            completedIndex={completedIndex}
            selectedIndex={selectedIndex}
            onChange={(grade) => onCompletedGradeChange(set.id, grade)}
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-x-3">
        <ol
          className={cn(
            "flex items-center -space-x-[3px]",
            unsolvable && !completedShown && "opacity-50",
          )}
        >
          {set.grades.map((grade, i) => {
            const reachable = !!cardGrades[i]?.reachable;
            const completed = i <= completedIndex;
            const diamond = (
              <GradeDiamond
                grade={grade.name}
                selected={i === selectedIndex}
                reachable={reachable}
                completed={completed}
                onSelect={() => setPickedIndex(i)}
                pickUnreachable
              />
            );
            const hintKey = gradeHintKey(cardGrades[i], completed);
            const hint = hintKey && t(hintKey);
            return (
              <li key={grade.name} className="relative flex items-center">
                {hint ? <Tooltip content={hint}>{diamond}</Tooltip> : diamond}
                {/* Points up at the grade the summary below is for. */}
                {i === selectedIndex && (
                  <svg
                    viewBox="-1 -1 12 8"
                    aria-hidden
                    className={cn(
                      "absolute -bottom-[11px] left-1/2 h-2 w-3 -translate-x-1/2",
                      completed ? "text-neutral-600" : GRADE_COLOR[grade.name],
                    )}
                  >
                    {/* White outline like the selected diamond's. */}
                    <path
                      d="M0 6h10L5 0Z"
                      fill="currentColor"
                      strokeWidth={1.25}
                      strokeLinejoin="round"
                      className="stroke-white"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ol>
        {/* Tokens the selected grade earns (every grade up to it), out of the set total. */}
        <span
          className={cn(
            "flex shrink-0 items-center gap-1.5 text-lg font-bold tabular-nums",
            completedShown && "opacity-50",
          )}
        >
          <GalleryTokenIcon className="size-6" />
          <span>{bestTokens}</span>
          <span className="text-sm font-normal text-foreground-muted">
            / {set.totalTokens}
          </span>
        </span>
      </div>
      {(completedShown || (showCollected && selected)) && (
        // An unreachable grade has no lineup: 0 of the set's cards, greyed.
        <div
          className={cn(
            "flex flex-col gap-y-1.5",
            completedShown
              ? "opacity-50"
              : !selected?.reachable && "opacity-40",
          )}
        >
          <div className="flex items-center justify-between text-xs">
            <span className={statLabel}>{t("list.collectedPlayers")}</span>
            <span className="font-semibold tabular-nums">
              {collected} / {collectedOf}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-raised">
            <div
              className={cn(
                "h-full rounded-full",
                completedShown || nothingToBuy
                  ? "bg-streamline-success"
                  : "bg-neutral-500",
              )}
              style={{
                width: `${collectedOf ? (collected / collectedOf) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}
      {unsolvable && !completedShown ? (
        <div
          className={cn(
            statBox,
            "min-h-[52px] flex-row gap-x-2 text-xs text-foreground-muted",
            refreshing && "animate-pulse",
          )}
        >
          <Ban className="size-4 shrink-0" />
          <span>{t("list.notSolvable")}</span>
        </div>
      ) : (
        <div
          className={cn(
            "grid grid-cols-3 gap-2",
            refreshing && "animate-pulse",
            completedShown && "opacity-50",
          )}
        >
          <div className="relative col-span-2 grid grid-cols-2 gap-2">
            <div
              className={cn(
                "contents",
                SHOW_NOTHING_TO_BUY_BANNER &&
                  (nothingToBuy || completedShown) &&
                  "[&>*]:opacity-10",
              )}
            >
              <GalleryStatTip
                icon={
                  <>
                    <span className="flex items-center gap-1 text-sm font-semibold tabular-nums">
                      {selected?.reachable && <CoinIcon size={14} />}
                      <span>
                        {selected?.reachable
                          ? compact.format(selected.budgetNeeded)
                          : "–"}
                      </span>
                    </span>
                    <span className={statLabel}>{t("list.budget")}</span>
                  </>
                }
              >
                <span className="whitespace-pre-line">
                  {t("list.budgetInfo")}
                </span>
              </GalleryStatTip>
              <GalleryStatTip
                icon={
                  <>
                    <span className="flex items-center gap-1 text-sm font-semibold tabular-nums">
                      {selected?.reachable && <CoinIcon size={14} />}
                      <span>
                        {selected?.reachable
                          ? compact.format(selected.taxLoss)
                          : "–"}
                      </span>
                    </span>
                    <span className={statLabel}>{t("solution.taxLoss")}</span>
                  </>
                }
              >
                {t("list.taxLossInfo")}
              </GalleryStatTip>
            </div>
            {SHOW_NOTHING_TO_BUY_BANNER && (nothingToBuy || completedShown) && (
              <div className="absolute inset-0 flex items-center justify-center gap-x-2 whitespace-pre-line rounded-xl border border-streamline-success/30 bg-streamline-success/10 px-2 text-xs leading-tight text-foreground">
                <Check className="size-4 shrink-0 text-streamline-success" />
                <span>
                  {completedShown
                    ? t("list.allCollected")
                    : t("list.nothingToBuy")}
                </span>
              </div>
            )}
          </div>
          <GalleryStatTip
            width={300}
            icon={
              <>
                <Verdict
                  verdict={verdict}
                  nothingToBuy={nothingToBuy && tokensGained > 0}
                />
                <span className={statLabel}>{t("list.verdict")}</span>
              </>
            }
          >
            <VerdictInfo />
          </GalleryStatTip>
        </div>
      )}
      <div
        className={cn(
          "mt-auto flex flex-col gap-y-1.5",
          completedShown && "opacity-50",
        )}
      >
        <div className="flex items-center gap-x-2">
          {/* A completed grade, or an unreachable one picked here, has no solution to open. */}
          {completedShown ? (
            <Button variant="tertiary" size="medium" stretch disabled>
              {t("solution.seeYours")}
            </Button>
          ) : unsolvable ||
            (selected &&
              !selected.reachable &&
              selectedIndex > completedIndex) ? (
            <Button variant="tertiary" size="medium" stretch disabled>
              {marketOnly ? t("solution.seeMarket") : t("solution.seeYours")}
            </Button>
          ) : (
            // Opens on the grade selected here.
            <NavLink
              to={`/fut-gallery/${categorySlug}/${set.slug}${selected ? `?grade=${selected.grade}` : ""}`}
              className="block flex-1"
            >
              <Button variant="tertiary" size="medium" stretch>
                {marketOnly ? t("solution.seeMarket") : t("solution.seeYours")}
              </Button>
            </NavLink>
          )}
        </div>
        {/* The footer row is always there, so the note on the right never changes the card's height. */}
        <div className="flex items-center justify-between gap-x-2">
          {completedShown ? null : progress?.solution ? (
            !unsolvable && (
              <p
                className={cn(
                  "flex items-center gap-1 text-xs",
                  progress.solution.stale
                    ? "text-amber-400"
                    : "text-foreground-muted",
                )}
              >
                {progress.solution.stale && <Clock className="size-3.5" />}
                <span>
                  {solved === "justNow"
                    ? t("solution.solvedJustNow")
                    : t("solution.solvedAgo", {
                        time: formatTime(solved ?? 0),
                      })}
                </span>
              </p>
            )
          ) : (
            <p className="mx-auto mt-0.5 flex items-center gap-1 text-xs text-amber-400/70">
              <TriangleAlert className="size-3.5 shrink-0" />
              <span>
                {clubImported
                  ? t("list.solveForYours")
                  : t("list.importForYours")}
              </span>
            </p>
          )}
          {!!user.uuid && !marketOnly && !completedShown && (
            <TextButton
              className="ml-auto underline"
              onClick={() => {
                setFirstOwnersOpen(true);
                // Only this open's reply counts; an earlier one may land late.
                const request = ++solutionRequest.current;
                setSolutionIds(undefined);
                getGallerySolution(set.id).then(
                  (solution) => {
                    if (request !== solutionRequest.current) return;
                    setSolutionIds(
                      solution?.grades
                        .find((g) => g.grade === selected?.grade)
                        ?.items.map((i) => i.resourceId),
                    );
                  },
                  () => undefined,
                );
              }}
            >
              {t("firstOwners.open")}
            </TextButton>
          )}
        </div>
        {refreshError && <p className="text-sm text-red-400">{refreshError}</p>}
      </div>
      {/* Mounted only while open: a closed sheet per card is ~127 idle listeners. */}
      {firstOwnersOpen && (
        <GalleryFirstOwnerSheet
          setId={set.id}
          setName={set.name}
          isOpen
          onClose={() => setFirstOwnersOpen(false)}
          onChanged={onFirstOwnersChanged}
          solutionIds={solutionIds}
        />
      )}
    </Card>
  );
});

/** A set-agnostic stand-in while the sets and the user's progress load. */
export const GallerySetCardSkeleton = () => (
  <Card hoverable={false} className="flex flex-col gap-y-4 p-4">
    <div className="flex items-center gap-x-3">
      <Skeleton className="size-12 rounded-full" />
      <div className="flex flex-col gap-y-1">
        <Skeleton className="w-32 text-base" />
        <Skeleton className="w-16 text-sm" />
      </div>
    </div>
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-[168px] rounded-md" />
      <Skeleton className="w-16 text-lg" />
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-[52px] w-full rounded-xl" />
      ))}
    </div>
    <Skeleton className="h-10 w-full rounded-[20px]" />
    <Skeleton className="w-24 text-xs" />
  </Card>
);
